import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";

// Agency pages
import Login from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Clients from "@/pages/admin/Clients";
import ClientDetail from "@/pages/admin/ClientDetail";
import Requests from "@/pages/admin/Requests";
import RequestDetail from "@/pages/admin/RequestDetail";
import TimeTracking from "@/pages/admin/TimeTracking";
import Team from "@/pages/admin/Team";
import Billing from "@/pages/admin/Billing";
import Invoices from "@/pages/admin/Invoices";
import InvoiceDetail from "@/pages/admin/InvoiceDetail";
import Profile from "@/pages/admin/Profile";
import Settings from "@/pages/admin/Settings";
import Messages from "@/pages/admin/Messages";
import Analytics from "@/pages/admin/Analytics";

// Portal pages
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalRequests from "@/pages/portal/PortalRequests";
import PortalRequestDetail from "@/pages/portal/PortalRequestDetail";
import PortalNewRequest from "@/pages/portal/PortalNewRequest";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalProfile from "@/pages/portal/PortalProfile";
import PortalOrganisation from "@/pages/portal/PortalOrganisation";

function AgencyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBF9F6" }}><div className="w-6 h-6 border-2 border-stone-300 border-t-stone-700 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/admin" replace />;
  if (user.role?.startsWith("client_")) return <Navigate to="/portal" replace />;
  return children;
}

function ClientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBF9F6" }}><div className="w-6 h-6 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role?.startsWith("agency_")) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <WebSocketProvider>
          <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<PortalLogin />} />
            <Route path="/portal/login" element={<Navigate to="/login" replace />} />
            <Route path="/admin" element={<Login />} />

            {/* Agency */}
            <Route path="/dashboard" element={<AgencyRoute><Dashboard /></AgencyRoute>} />
            <Route path="/clients" element={<AgencyRoute><Clients /></AgencyRoute>} />
            <Route path="/clients/:id" element={<AgencyRoute><ClientDetail /></AgencyRoute>} />
            <Route path="/requests" element={<AgencyRoute><Requests /></AgencyRoute>} />
            <Route path="/requests/:id" element={<AgencyRoute><RequestDetail /></AgencyRoute>} />
            <Route path="/time-tracking" element={<AgencyRoute><TimeTracking /></AgencyRoute>} />
            <Route path="/team" element={<AgencyRoute><Team /></AgencyRoute>} />
            <Route path="/billing" element={<AgencyRoute><Billing /></AgencyRoute>} />
            <Route path="/invoices" element={<AgencyRoute><Invoices /></AgencyRoute>} />
            <Route path="/invoices/:id" element={<AgencyRoute><InvoiceDetail /></AgencyRoute>} />
            <Route path="/settings" element={<AgencyRoute><Settings /></AgencyRoute>} />
            <Route path="/messages" element={<AgencyRoute><Messages /></AgencyRoute>} />
            <Route path="/analytics" element={<AgencyRoute><Analytics /></AgencyRoute>} />
            <Route path="/profile" element={<AgencyRoute><Profile /></AgencyRoute>} />

            {/* Portal */}
            <Route path="/portal" element={<ClientRoute><PortalDashboard /></ClientRoute>} />
            <Route path="/portal/requests" element={<ClientRoute><PortalRequests /></ClientRoute>} />
            <Route path="/portal/requests/new" element={<ClientRoute><PortalNewRequest /></ClientRoute>} />
            <Route path="/portal/requests/:id" element={<ClientRoute><PortalRequestDetail /></ClientRoute>} />
            <Route path="/portal/invoices" element={<ClientRoute><PortalInvoices /></ClientRoute>} />
            <Route path="/portal/invoices/:invoiceId" element={<ClientRoute><PortalInvoices /></ClientRoute>} />
            <Route path="/portal/messages" element={<ClientRoute><PortalMessages /></ClientRoute>} />
            <Route path="/portal/profile" element={<ClientRoute><PortalProfile /></ClientRoute>} />
            <Route path="/portal/organisation" element={<ClientRoute><PortalOrganisation /></ClientRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </WebSocketProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
